import { CursorArrowRippleIcon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import { Balance, FlexListItem, HoverCard, Percentage, Text, toolkitCx } from "@sundae/ui-toolkit";
import { IAvatarProps } from "@sundae/ui-toolkit/dist/types/components/atoms/Avatar/Avatar";
import { IHoverCardProps } from "@sundae/ui-toolkit/dist/types/components/organisms/HoverCard/HoverCard";
import { AssetAmount } from "@sundaeswap/asset";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import cx from "classnames";
import { FC, PropsWithChildren, useCallback, useMemo, useState } from "react";

import { CARDANO_EXPLORER_LINK_MAP } from "../../constants/cardano.constants";
import { Asset, PoolFragmentFragment } from "../../gql/generated/stats2.sdk";
import { useWalletPositions } from "../../gql/hooks/positions.query";
import { useProfileAppContext } from "../../stores";
import { getAssetName, getQAsset } from "../../utils/assets.utils";
import { featureIsEnabled } from "../../utils/features.utils";
import { divideBy } from "../../utils/number-format";
import { getPoolName } from "../../utils/pool.utils";
import { truncateString } from "../../utils/string-format";
import AssetWithAssetName from "../AssetWithAssetName";
import CardanoExplorer from "../CardanoExplorer";
import PoolBadges from "../PoolBadges";

export interface IAssetAvatarProps extends Omit<IAvatarProps, "alt" | "src" | "pointer"> {
  hoverCardProps?: IHoverCardProps;
  pool?: PoolFragmentFragment;
  small?: boolean;
}

const PoolHoverCard: FC<PropsWithChildren<IAssetAvatarProps>> = ({
  children,
  hoverCardProps,
  pool,
  small,
  ...avatarProps
}) => {
  const { currentLocale, t } = useI18N();
  const [open, setOpen] = useState(false);
  const { positions } = useWalletPositions({
    page: 0,
    pageSize: 40, // Set it to a high number to fetch all positions.
  });
  const {
    state: { cardanoExplorer },
  } = useProfileAppContext();

  const position = useMemo(
    () => positions?.stats.find((position) => position.pool.ident === pool?.ident),
    [positions]
  );

  const assetA = pool?.assetA;
  const assetB = pool?.assetB;
  const assetAQAsset = getQAsset(assetA?.assetId ?? "");
  const assetBQAsset = getQAsset(assetB?.assetId ?? "");
  const quantityA = useMemo(
    () => new AssetAmount(pool?.quantityA ?? 0, assetA?.decimals),
    [assetA, pool?.quantityA]
  );
  const quantityB = useMemo(
    () => new AssetAmount(pool?.quantityB ?? 0, assetB?.decimals),
    [assetB, pool?.quantityB]
  );

  const getAssetIdItem = useCallback((asset: Asset, id: number) => {
    const isAdaAsset = SundaeUtils.isAdaAsset(asset);
    const value = isAdaAsset ? (
      <Text tag="span" size="sm">
        -
      </Text>
    ) : (
      <CardanoExplorer
        hash={asset.assetId}
        size="sm"
        type={CARDANO_EXPLORER_LINK_MAP.asset[cardanoExplorer]}
      />
    );

    return {
      id,
      label: (
        <Text tag="span" size="sm" variant="muted">
          {t("assetId", { ns: "assets" })} {getAssetName(asset)}
        </Text>
      ),
      value,
    };
  }, []);

  const memoizedHoverCardItems = useMemo(() => {
    if (!pool || !assetA || !assetB) return [];

    return [
      {
        id: 1,
        label: t("pool", { context: "ident", ns: "glossary" }),
        value: featureIsEnabled("v3Contracts") ? truncateString(pool.ident, 10, 8) : pool.ident,
      },
      ...[getAssetIdItem(assetAQAsset ?? pool.assetA, 2)],
      ...[getAssetIdItem(assetBQAsset ?? pool.assetB, 3)],
      ...(featureIsEnabled("v3Contracts")
        ? [
            {
              id: 4,
              label: t("version", { ns: "glossary" }),
              value: (
                <PoolBadges badges={{ version: true, ident: false, lpFee: false }} pool={pool} />
              ),
            },
          ]
        : []),
      {
        id: 5,
        label: t("fee", { ns: "fees", context: "lp" }),
        value: <Percentage locale={currentLocale} size="sm" value={divideBy(pool.fee, 100) || 0} />,
      },
      {
        id: 6,
        label: t("poolQuantity", {
          ns: "liquidity",
          assetName: getAssetName(pool.assetA),
        }),
        value: (
          <Balance
            locale={currentLocale}
            showSymbol={SundaeUtils.isAdaAsset(pool.assetA)}
            size="sm"
            value={quantityA}
          />
        ),
      },
      {
        id: 7,
        label: t("poolQuantity", {
          ns: "liquidity",
          assetName: getAssetName(pool.assetB),
        }),
        value: (
          <Balance
            locale={currentLocale}
            showSymbol={SundaeUtils.isAdaAsset(pool.assetB)}
            size="sm"
            value={quantityB}
          />
        ),
      },
    ];
  }, [assetA, assetB, cardanoExplorer, currentLocale, pool, quantityA, quantityB, t]);

  if (!pool || !assetA || !assetB) return <>{children}</>;

  return (
    <HoverCard
      {...hoverCardProps}
      open={open}
      onOpenChange={setOpen}
      primitiveProps={{
        content: {
          ...hoverCardProps?.primitiveProps?.content,
          className: cx("min-w-[17.5rem]", "md:min-w-[20rem]"),
        },
        trigger: {
          ...hoverCardProps?.primitiveProps?.trigger,
          asChild: true,
        },
      }}
      trigger={
        <div
          className={cx("relative flex flex-wrap items-center gap-2 rounded-full", {
            "motion-safe:animate-shadow-pulse": open,
          })}
        >
          {children}

          <CursorArrowRippleIcon
            className={cx(
              "absolute -bottom-[6px] z-[9] hidden lg:block",
              {
                "left-6 h-3 w-3": small,
                "left-7 h-4 w-4": !small,
                "opacity-0": open,
                "opacity-50 dark:opacity-100": !open,
              },
              toolkitCx.layout.text.default,
              toolkitCx.layout.transition.opacity
            )}
          />
        </div>
      }
    >
      <div className="relative z-10 flex h-full w-full items-center gap-2 motion-safe:animate-slide-down">
        <div className="grid w-full will-change-transform" key="PoolHoverCard--Content">
          <div className="grid gap-2 will-change-transform" key="PoolHoverCard--AvatarGroup">
            <AssetWithAssetName
              {...avatarProps}
              assets={[assetAQAsset ?? assetA, assetBQAsset ?? assetB]}
              assetName={getPoolName({
                assetA: assetAQAsset ?? assetA,
                assetB: assetBQAsset ?? assetB,
              })}
              size="md"
            />
          </div>

          <div className="mt-3 w-full will-change-transform">
            <Text tag="h3">{t("liquidity", { ns: "liquidity", context: "information" })}</Text>

            <ul className="mt-2 flex w-full flex-col gap-2 will-change-transform">
              {memoizedHoverCardItems.map(({ id, label, value }, index) => {
                return (
                  <li className="will-change-transform" key={`${index}-${id}`}>
                    <FlexListItem
                      field={{
                        label,
                        variant: "muted",
                      }}
                      value={{ label: value }}
                    />
                  </li>
                );
              })}

              {position && (
                <li className="will-change-transform">
                  <FlexListItem
                    field={{
                      label: t("share", { ns: "common", context: "pool" }),
                      variant: "muted",
                    }}
                    value={{
                      label: (
                        <Percentage
                          formatOpts={{ minimumFractionDigits: 0, maximumFractionDigits: 4 }}
                          locale={currentLocale}
                          size="sm"
                          value={position.poolShare}
                        />
                      ),
                    }}
                  />
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </HoverCard>
  );
};

export default PoolHoverCard;
